    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Biometric" title="Biometric Connectors" subtitle="Manage biometric providers, service credentials, sync runs, and imported attendance punches before they flow into HR timesheets.">
        <div actions>
          <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()" [disabled]="loading"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'connections', label: 'Connections', icon: 'fa-solid fa-plug', badge: connections.length },
          { key: 'providers', label: 'Provider Marketplace', icon: 'fa-solid fa-store', badge: providers.length },
          { key: 'runs', label: 'Sync Runs', icon: 'fa-solid fa-arrows-rotate', badge: runs.length },
          { key: 'events', label: 'Imported Punches', icon: 'fa-solid fa-fingerprint', badge: events.length }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <section class="biometric-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'providers'" title="Provider Marketplace" subtitle="Supported biometric connector templates" [resultText]="providers.length + ' providers'">
          <div class="provider-grid">
            @for (provider of providers; track provider.providerCode) {
              <div class="provider-card">
                <div class="provider-head">
                  <div>
                    <strong>{{ provider.displayName }}</strong>
                    <div class="provider-meta">{{ provider.providerCode }}</div>
                  </div>
                  <p-tag [value]="provider.supportsApiSync ? 'API Sync' : 'Manual'" [severity]="provider.supportsApiSync ? 'success' : 'warn'"></p-tag>
                </div>
                <p>{{ provider.description || 'Biometric provider connector.' }}</p>
                <div class="provider-actions">
                  <button pButton type="button" size="small" label="Create Connection" icon="pi pi-plus" (click)="openConnection(provider)" [disabled]="!canManage"></button>
                  @if (provider.documentationUrl) {
                    <a [href]="provider.documentationUrl" target="_blank" rel="noreferrer">Docs</a>
                  }
                </div>
              </div>
            }
          </div>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'connections'" title="Connections" subtitle="Tenant-scoped provider credentials and sync settings" [resultText]="connections.length + ' connections'">
          <div actions><button pButton type="button" icon="pi pi-plus" label="New Connection" (click)="openConnection()" [disabled]="!canManage"></button></div>
          <p-table [value]="connections" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '980px' }">
            <ng-template pTemplate="header">
              <tr><th>Name</th><th>Provider</th><th>Status</th><th>Base URL</th><th>Last Sync</th><th style="width: 12rem">Actions</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-connection>
              <tr>
                <td><strong>{{ connection.displayName }}</strong><br><small>{{ connection.connectionId }}</small></td>
                <td>{{ connection.providerCode }}</td>
                <td><p-tag [value]="format(connection.status)" [severity]="connection.active ? 'success' : 'warn'"></p-tag></td>
                <td><small>{{ connection.baseUrl || '-' }}</small></td>
                <td>{{ connection.lastSyncAt ? (connection.lastSyncAt | date:'short') : '-' }}<br><small>{{ connection.lastSyncStatus || '-' }}</small></td>
                <td>
                  <div class="row-actions">
                    <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" (click)="openConnection(undefined, connection)" [disabled]="!canManage"></button>
                    <button pButton type="button" icon="pi pi-check-circle" [rounded]="true" [text]="true" severity="secondary" (click)="test(connection)" [disabled]="!canManage || !connection.connectionId"></button>
                    <button pButton type="button" icon="pi pi-sync" [rounded]="true" [text]="true" severity="success" (click)="sync(connection)" [disabled]="!canManage || !connection.connectionId"></button>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">No biometric connections found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      <section class="biometric-grid lower">
        <app-page-data-panel [hidden]="activeListTab !== 'runs'" title="Sync Runs" subtitle="Biometric ingestion and connector execution history" [resultText]="runs.length + ' runs'">
          <p-table [value]="runs" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '960px' }">
            <ng-template pTemplate="header">
              <tr><th>Run</th><th>Provider</th><th>Status</th><th>Received</th><th>Accepted</th><th>Duplicates</th><th>Failed</th><th>Completed</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-run>
              <tr>
                <td><strong>{{ run.runType || '-' }}</strong><br><small>#{{ run.runId }}</small></td>
                <td>{{ run.providerCode || '-' }}</td>
                <td><p-tag [value]="format(run.status)" [severity]="statusSeverity(run.status)"></p-tag></td>
                <td>{{ run.receivedCount ?? 0 }}</td>
                <td>{{ run.acceptedCount ?? 0 }}</td>
                <td>{{ run.duplicateCount ?? 0 }}</td>
                <td>{{ run.failedCount ?? 0 }}</td>
                <td>{{ run.completedAt ? (run.completedAt | date:'short') : '-' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="8">No biometric sync runs found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'events'" title="Imported Punches" subtitle="Latest biometric punches mapped into HR attendance" [resultText]="events.length + ' imports'">
          <p-table [value]="events" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1120px' }">
            <ng-template pTemplate="header">
              <tr><th>Employee</th><th>Punch Time</th><th>Event</th><th>Device</th><th>Status</th><th>Reference</th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-event>
              <tr>
                <td><strong>{{ event.employeeName || event.externalEmployeeName || '-' }}</strong><br><small>{{ event.employeeNumber || event.externalEmployeeId || event.employeeId }}</small></td>
                <td>{{ event.punchTime ? (event.punchTime | date:'short') : '-' }}</td>
                <td>{{ format(event.mappedEventType || event.providerEventTypeCode) }}<br><small>{{ event.externalDepartmentName || '-' }}</small></td>
                <td>{{ event.deviceId || '-' }}</td>
                <td><p-tag [value]="format(event.importStatus)" [severity]="statusSeverity(event.importStatus)"></p-tag></td>
                <td><small>{{ event.sourceReference || '-' }}</small></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">No biometric imports found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>
    </app-page-shell>

    <p-dialog [(visible)]="connectionDialogVisible" [modal]="true" [style]="{ width: '48rem' }" [header]="editingConnectionId ? 'Edit Biometric Connection' : 'New Biometric Connection'">
      <div class="dialog-grid">
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-store"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-select inputId="providerCode" [options]="providerOptions" optionLabel="label" optionValue="value" [(ngModel)]="connectionForm.providerCode" styleClass="w-full" (ngModelChange)="onProviderChanged($event)"></p-select>
            <label for="providerCode">Provider</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-signature"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="displayName" pInputText [(ngModel)]="connectionForm.displayName" />
            <label for="displayName">Display name</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-link"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="baseUrl" pInputText [(ngModel)]="connectionForm.baseUrl" />
            <label for="baseUrl">Base URL</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-key"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="accessKey" pInputText [(ngModel)]="connectionForm.accessKey" />
            <label for="accessKey">Access key</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-route"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="reportPath" pInputText [(ngModel)]="connectionForm.reportPath" />
            <label for="reportPath">Report path</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-calendar-week"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <p-inputNumber inputId="syncWindowDays" [(ngModel)]="connectionForm.syncWindowDays" [min]="1" [max]="90"></p-inputNumber>
            <label for="syncWindowDays">Sync window days</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-desktop"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="snName" pInputText [(ngModel)]="connectionForm.snName" />
            <label for="snName">Device filter (snName)</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup>
          <p-inputGroupAddon><i class="fa-solid fa-arrows-turn-to-dots"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <input id="defaultEventType" pInputText [(ngModel)]="connectionForm.defaultEventType" />
            <label for="defaultEventType">Default HR event type</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup class="wide">
          <p-inputGroupAddon><i class="fa-solid fa-code"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <textarea id="eventTypeMappingsJson" pTextarea rows="3" [(ngModel)]="connectionForm.eventTypeMappingsJson"></textarea>
            <label for="eventTypeMappingsJson">Event type mappings JSON</label>
          </p-iftaLabel>
        </p-inputGroup>
        <p-inputGroup class="wide">
          <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
          <p-iftaLabel>
            <textarea id="notes" pTextarea rows="3" [(ngModel)]="connectionForm.notes"></textarea>
            <label for="notes">Notes</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" label="Cancel" severity="secondary" [outlined]="true" (click)="connectionDialogVisible = false"></button>
        <button pButton type="button" label="Save" icon="pi pi-save" (click)="saveConnection()" [disabled]="!canManage || !connectionForm.providerCode || !connectionForm.displayName"></button>
      </ng-template>
    </p-dialog>
